import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'config/axios';
import { useNavigate, useParams } from 'react-router-dom';
// import { GoArrowLeft } from "react-icons/go";
import './index.css';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Header from 'components/Header';
import InfoBox from 'components/InfoBox';
import LevelBar from 'components/LevelBar';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Dropdown3 from 'components/Dropdown3';

function CustomTooltip({ payload, active }) {
    if (active && payload && payload.length) {
        const data = payload[0].payload; // 막대의 데이터
        const testDate = new Date(data.testDate).toLocaleDateString();
        const englishScore = data.englishScore;
        const mathScore = data.mathScore;
        const totalScore = data.totalScore;

        return (
            <div className="custom-tooltip">
                <p>Test Date: {testDate}</p>
                <p style={{ color: '#82ca9d' }}>English: {englishScore}</p>
                <p style={{ color: '#82ca9d' }}>Math: {mathScore}</p>
                <p style={{ color: '#8884d8', fontWeight:'bold', fontSize: '12'}}>Total: {totalScore}</p>
            </div>
        );
    }
    return null;
}

const CustomBar = (props) => {
    const { x, y, width, height} = props;
    const upperHeight = 6; // 위쪽 부분의 높이

    return (
        <g>
            <rect x={x} y={y} width={width} height={upperHeight} fill={`var(--deepGreen)`} />
            <rect x={x} y={y + upperHeight} width={width} height={height - upperHeight} fill="#E8E8E8" />
        </g>
    );
};

const CustomTick = ({ x, y, payload, fontSize, fontWeight, fill }) => {
    const lines = payload.value.split('\n');
    const marginAbove = 10;
    return (
        <g transform={`translate(${x},${y + marginAbove})`}>
        <text 
          x={0} 
          y={0} 
          dy={16} 
          textAnchor="middle" 
          fill={fill} 
          fontSize={fontSize} 
          fontWeight={fontWeight}
        >
          {lines.map((line, index) => (
            <tspan key={index} x="0" dy={index === 0 ? 0 : 16}>{line}</tspan>
          ))}
        </text>
      </g>
    );
  };
    
const TEST_TYPE = ['SHSAT', 'SAT', 'ACT']
const ACT_TEST_TYPE = ['SHSAT', 'ACT']
const SAT_TEST_TYPE = ['SHSAT', 'SAT']
const SHSAT_TEST_TYPE = ['SHSAT']

function HistoricalSHSATReport() {
    const [testData, setTestData] = useState(null);
    const [isACTActive, setIsACTActive] = useState(false);
    const [isSATActive, setIsSATActive] = useState(false);
    const [studentName, setStudentName] = useState('');
    const [organization, setOrganization] = useState('');
    const [since, setSince] = useState('');
    const [tests, setTests] = useState([]);
    const [last10Tests, setLast10Tests] = useState([]);
    const [allTests, setAllTests] = useState([]);
    const [lastTest, setLastTest] = useState(null);
    const [modules, setModules] = useState([]);
    const [englishRootCategories, setEnglishRootCategories] = useState([]);
    const [mathRootCategories, setMathRootCategories] = useState([]);
    const [avgCorrectPercent, setAvgCorrectPercent] = useState({});
    const [avgAllTests, setAvgAllTests] = useState(0);
    const [avgAllTestsDenom, setAvgAllTestsDenom] = useState(0);
    const [avgEnglishTests, setAvgEnglishTests] = useState(0);
    const [avgEnglishTestsDenom, setAvgEnglishTestsDenom] = useState(0);
    const [avgMathTests, setAvgMathTests] = useState(0);
    const [avgMathTestsDenom, setAvgMathTestsDenom] = useState(0);
    const [avgTestDuration, setAvgTestDuration] = useState(0);
    // const [avgPercentile, setAvgPercentile] = useState(0);
    // const [totalMaxScore, setTotalMaxScore]=useState(0);
    // const [totalMinScore, setTotalMinScore] = useState(0);
    const [totalScoreRange, setTotalScoreRange] = useState([]);
    const [englishScoreRange, setEnglishScoreRange] = useState([]);
    const [mathScoreRange, setMathScoreRange] = useState([]);
    const [fontSize, setFontSize] = useState(12);
    const [smallFontSize, setSmallFontSize] = useState(0);
    const [scrollStandard, setScrollStandard] = useState(10);
    const [testType, setTestType] = useState(TEST_TYPE[0]);
    const navigate = useNavigate();
    const { studentId } = useParams();
    const scrollRef = useRef(null); 
    const printRef = useRef(null);

    // Print the live DOM natively (the @media print stylesheet isolates the
    // report) so charts render — react-to-print's clone dropped them.
    const handlePrint = () => {
        // Scope print isolation to this page only: the @media print rules key
        // off this body class, so other pages' printing is never affected.
        document.body.classList.add('printing-shsat-historical-report');
        // Remove the browser's default page margins for this print only, which
        // also drops the auto-added header/footer (date, title, URL, page #).
        // Injected here and removed on afterprint so other prints keep theirs.
        const pageStyle = document.createElement('style');
        pageStyle.textContent = '@page { margin: 0; }';
        document.head.appendChild(pageStyle);
        const cleanup = () => {
            document.body.classList.remove('printing-shsat-historical-report');
            pageStyle.remove();
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
    };

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth <= 768) {
          setFontSize(10); 
          setSmallFontSize(8);
        } else {
          setFontSize(12); 
          setSmallFontSize(10);
        }

        if(window.innerWidth <= 480) {
            setScrollStandard(3);
        } else if (window.innerWidth <= 576) {
            setScrollStandard(4);
        } else if(window.innerWidth <= 768){
            setScrollStandard(6);
        } else{
            setScrollStandard(10);
        }
      };
  
      window.addEventListener('resize', handleResize);
      handleResize(); 
      return () => window.removeEventListener('resize', handleResize); 
    }, [scrollStandard]);

    useEffect(()=>{
        axios.get(`tutors/digital/shsat/historicalResult/${studentId}/`)
        .then(res => {
            setTestData(res.data);
            setIsACTActive(res.data.isACTActiveHistoricalReport);
            setIsSATActive(res.data.isSATActiveHistoricalReport);
            setStudentName(res.data.studentName);
            setOrganization(res.data.organization);
            setSince(res.data.since);
            setTests(res.data.last5tests);
            setLast10Tests(res.data.last10Tests);
            setAllTests(res.data.allTests);
            setLastTest(res.data.lastTest);
            setModules(res.data.lastTest.testModules);
            // setEnglishRootCategories(res.data.lastTest.testSections.find(section=>section.subject==="Engish Language Arts").rootCategories)
            // setMathRootCategories(res.data.lastTest.testSections.find(section=>section.subject==="Math").rootCategories)
            setEnglishRootCategories(res.data.englishRootCategories);
            setMathRootCategories(res.data.mathRootCategories);
            // setTestSections(res.data.lastTest.testSections);
            setAvgCorrectPercent(res.data.avgCorrectPercent);
            setAvgAllTests(res.data.avgAllTests);
            setAvgAllTestsDenom(res.data.avgAllTestsDenom);
            setAvgEnglishTests(res.data.avgEnglishTests);
            setAvgEnglishTestsDenom(res.data.avgEnglishTestsDenom);
            setAvgMathTests(res.data.avgMathTests);
            setAvgMathTestsDenom(res.data.avgMathTestsDenom);
            // setAvgPercentile(res.data.avgPercentile);
            setAvgTestDuration(res.data.avgTestDuration);
            setTotalScoreRange(res.data.totalScoreRange);
            setEnglishScoreRange(res.data.englishScoreRange);
            setMathScoreRange(res.data.mathScoreRange);
            // setTotalMaxScore(parseInt(res.data.totalMaxScore));
            // setTotalMinScore(parseInt(res.data.totalMinScore));
        })
        .catch(error => {
            console.log(error);
        });
    }, [testType]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth; 
        }
    }, [allTests]);

    const formatDuration = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        let result = '';

        if (minutes>0){
            result+=`${minutes}m `;
        }
        if (seconds>0){
            result+=`${seconds}s`;
        }
        return result;
    };

    const formatDurationSecOnly = (totalSeconds) => {
        const minutes = Math.round(totalSeconds / 60);
        return `${minutes} min`;
    };

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    Performance History (SHSAT) - Test Center Admin
                </title>
            </Helmet>
            {tests.length>0?
                <div className='historicalSHSATReport__wrap'>
                    <Header
                        title={'Performance History'}
                        prevRoutes={[{ name: 'All Students', path: '/students' },
                                    { name: `${studentName}`, path: `/students/${studentId}` }
                        ]}
                    />
                    <div className='historicalSHSATReport__print'>
                        <Button
                            onClick={handlePrint}
                        >
                            Print Report
                        </Button>
                        <Dropdown3
                            data={(isACTActive && isSATActive)
                                ? TEST_TYPE
                                : isACTActive 
                                ? ACT_TEST_TYPE  
                                : isSATActive
                                ? SAT_TEST_TYPE
                                : SHSAT_TEST_TYPE
                            }
                            value={testType}
                            onSelect={value => {
                                if (value === 'ACT') {
                                    navigate(`/students/${studentId}/digital-act/performanceHistory`);
                                } else if (value === 'SAT') {
                                    navigate(`/students/${studentId}/digital-sat/performanceHistory`);
                                }
                                setTestType(value);
                            }}
                        />
                    </div>  
                    {/* Report main */}
                    <div className='historicalSHSATReport__report' ref={printRef}>
                        <div className='historicalSHSATReport__headerRowInfo'>
                            <div className='historicalSHSATReport__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='historicalSHSATReport__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='historicalSHSATReport__titleRight'>
                                <h1 className='historicalSHSATReport__title'>Performance History</h1>
                                <h2 className='historicalSHSATReport__studentName'>{studentName}</h2>  
                                <h2 className='historicalSHSATReport__studentEmail'>{testData.email}</h2>    
                            </div>
                        </div>
                        {/* Basic information */}
                        <div className='historicalSHSATReport__reportSection'>
                            <div className='historicalSHSATReport__sectionTitle'>Basic Information</div>
                            <div className='historicalSHSATReport__sectionContent'>
                                <InfoBox title="Name" content = {studentName}/>
                                <InfoBox title="Organization" content = {organization}/>
                                <InfoBox title="Since" content = {since}/>
                                <InfoBox title="Last Test Duration" content = {formatDuration(lastTest?.testDuration)} />
                            </div>
                        </div>

                        {/* Last Test */}
                        <div className='historicalSHSATReport__reportSection'>
                            <div className='historicalSHSATReport__sectionTitleWrap'>
                                <div className='historicalSHSATReport__sectionTitle'>Latest Test</div>
                                <div className='historicalSHSATReport__type'>{lastTest?.testType}</div>
                                {/* <div className='historicalSHSATReport__date'>{lastTest?.testDate}</div> */}
                            </div>
                            <div className='historicalSHSATReport__sectionContent2'>
                                <InfoBox middle title="Test Date" content = {lastTest?.testDate2}/>
                                <InfoBox big title="Total Score" content = {lastTest?.totalScore}/>
                                {/* <InfoBox big title="% Percentile" content = {Math.round(lastTest?.percentile)}/> */}
                                <InfoBox big title="Engish Language Arts" content = {lastTest?.englishScore}/>
                                <InfoBox big title="Mathematics" content = {lastTest?.mathScore}/>
                                <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__sectionModule`}>
                                    <table className='historicalSHSATReport__table'>
                                        <thead>
                                            <tr className='historicalSHSATReport__tableHeader'>
                                                <th className='historicalSHSATReport__tableRowName'>Score by Subject</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalSHSATReport__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {modules?.map((data,i)=>(
                                            <tr>
                                                <td className='historicalSHSATReport__tableRowName'>{data.subject}
                                                    {data.moduleNumber===2?(data.hard?' (Hard)': ' (Easy)'):null}</td>
                                                <td>{data.rawScore}</td>
                                                <td>{data.numQuestions}</td>
                                                <td>{Math.round(data.rawScore/data.numQuestions*100)}%</td>
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                                {/* <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__sectionEnglish`}>
                                    <table className='historicalSHSATReport__table'>
                                        <thead>
                                            <tr className='historicalSHSATReport__tableHeader'>
                                                <th className='historicalSHSATReport__tableRowName'>Score by English Section</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalSHSATReport__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {lastTest.testSections.find(section=>section.subject==="Engish Language Arts")?.rootCategories&&
                                        Object.entries(lastTest.testSections.find(section=>section.subject==="Engish Language Arts").rootCategories)?.map(([category, data],i)=>(
                                            <tr>
                                                <td className='historicalSHSATReport__tableRowName'>{category || '-'}</td>
                                                <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                <td>
                                                    {data && data['numQuestions'] > 0 
                                                        ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </table>
                                </div>
                                <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__sectionMath`}>
                                    <table className='historicalSHSATReport__table'>
                                        <thead>
                                            <tr className='historicalSHSATReport__tableHeader'>
                                                <th className='historicalSHSATReport__tableRowName'>Score by Math Section</th>
                                                <th># Correct</th>
                                                <th># Total</th>
                                                <th>% Percent</th>
                                            </tr>
                                        </thead>
                                        <tr className="historicalSHSATReport__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {lastTest.testSections.find(section=>section.subject==="Mathematics")?.rootCategories&&
                                        Object.entries(lastTest.testSections.find(section=>section.subject==="Mathematics").rootCategories)?.map(([category, data],i)=>(
                                            <tr>
                                                <td className='historicalSHSATReport__tableRowName'>{category || '-'}</td>
                                                <td>{data && data['numCorrect'] !== undefined ? data['numCorrect'] : '-'}</td>
                                                <td>{data && data['numQuestions'] !== undefined ? data['numQuestions'] : '-'}</td>
                                                <td>
                                                    {data && data['numQuestions'] > 0 
                                                        ? `${Math.round((data['numCorrect'] / data['numQuestions']) * 100)}%` 
                                                        : '-'}
                                                </td>                                            
                                            </tr>
                                        ))}
                                    </table>
                                </div> */}
                            </div>
                        </div>

                        {/* Category Scores — Level 1 domains for the latest
                            test, the same ones shown on the test result report. */}
                        {lastTest?.testSections?.some(section => section.rootCategories && Object.keys(section.rootCategories).length > 0) &&
                            <div className='historicalSHSATReport__reportSection'>
                                <div className='historicalSHSATReport__sectionTitle'>Category Scores</div>
                                <div className='historicalSHSATReport__sectionBox' style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                                    {lastTest.testSections.map((section, s) => (
                                        <div key={s} style={{ flex: 1, minWidth: 280 }}>
                                            <h4 style={{ color: 'var(--gray500)', fontWeight: 400, marginBottom: 14 }}>{section.subject}</h4>
                                            {Object.entries(section.rootCategories || {}).map(([name, data]: [string, any], i) => (
                                                <div key={i} style={{ marginBottom: 14 }}>
                                                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{name}</div>
                                                    <LevelBar
                                                        className={s % 2 === 0 ? 'LevelBar__greenBox' : 'LevelBar__orangeBox'}
                                                        percent={data.numQuestions ? 100 * data.numCorrect / data.numQuestions : 0}
                                                        label={`${data.numCorrect}/${data.numQuestions}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }

                        {/* Achievements */}
                        <div className='historicalSHSATReport__reportSection'>
                            <div className='historicalSHSATReport__sectionTitle historicalSHSATReport__pageBreak'>Achievements</div>
                            <div className='historicalSHSATReport__sectionContent3'>
                                {/* <InfoBox big title="Average % Percentile" content = {Math.round(avgPercentile)} range={[0, 100]}/> */}
                                <InfoBox big title="All Tests Avg" content = {`${avgAllTests}/${avgAllTestsDenom}`} />
                                <InfoBox big title="English Avg" content = {avgEnglishTests !== null ? `${avgEnglishTests}/${avgEnglishTestsDenom}` : '-'} />
                                <InfoBox big title="Math Avg" content = {avgMathTests !== null ? `${avgMathTests}/${avgMathTestsDenom}` : '-'} />
                                <InfoBox big title="Test Duration Avg" content = {formatDurationSecOnly(avgTestDuration)}/>
                                {/* <InfoBox big title="Number of Tests" content = {allTests.length}/>                              */}
                                {/* graph */}
                                <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__graph`}>
                                    <div className='historicalSHSATReport__SectionBoxHeader'>
                                        <div className='historicalSHSATReport__Sectiontitle'>Recent Tests Progress</div>
                                        <div className='historicalSHSATReport__graphNum historicalSHSATReport__textAll'>All Tests</div>
                                        <div className='historicalSHSATReport__graphNum historicalSHSATReport__textFew'>Last 10 Tests</div>
                                    </div>
                                    <div
                                        ref={scrollRef}
                                        style={{
                                        width: "100%",
                                        height: "95%",
                                        overflowX: allTests.length>scrollStandard? "auto":"hidden" 
                                        }}
                                        className='historicalSHSATReport__normalGraph'
                                    >
                                        <ResponsiveContainer width={allTests.length>scrollStandard? allTests.length*100:"100%"} height="95%">
                                            <BarChart
                                                data={allTests}
                                                margin={{
                                                    top: 30,
                                                    right: 0,
                                                    left: 0,
                                                    bottom: 10,
                                                }}
                                                barSize={fontSize*2}
                                                barCategoryGap={smallFontSize*2}
                                            >
                                                <XAxis dataKey="testInfo"
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }}
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={(props) => <CustomTick {...props} fontSize={`${fontSize}px`} fontWeight="bold" fill="var(--gray400)" />}                                                    
                                                    interval={0} // 모든 라벨을 보여줌
                                                    />
                                                <YAxis 
                                                    ticks={[0, totalScoreRange[1]]} 
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }} 
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={{ fontSize: `${fontSize}px`, fontWeight: 'bold', fill: 'var(--gray400)' }} />
                                                <Tooltip content={<CustomTooltip payload={undefined} active={undefined} />} />
                                                <Bar dataKey="englishScore" 
                                                    fill="#82ca9d" 
                                                    label={(props) => {
                                                        const { x, y, width, value } = props;
                                                        const textX = x + width/2+3; 
                                                        const textY = y - fontSize+8; // 라벨의 Y 위치
                                                        return (
                                                            <text 
                                                                x={textX} 
                                                                y={textY} 
                                                                fill="#000" 
                                                                fontSize={smallFontSize} 
                                                                fontWeight={400} 
                                                                textAnchor="start" 
                                                                transform={`rotate(270, ${textX}, ${textY})`} 
                                                            >
                                                                {value&&'ELA'} <tspan fontWeight={700} >{value}</tspan>
                                                            </text>
                                                        );
                                                    }} 
                                                    shape={<CustomBar />}
                                                    // activeBar={<Rectangle fill="pink" stroke="blue" />} 
                                                />
                                                <Bar dataKey="mathScore" 
                                                    fill="#82ca9d" 
                                                    label={(props) => {
                                                        const { x, y, width, height, value } = props;
                                                        const textX = x + width/2+2; 
                                                        const textY = y -smallFontSize+8; // 라벨의 Y 위치
                                                        return (
                                                            <text 
                                                                x={textX} 
                                                                y={textY} 
                                                                fill="#000" 
                                                                fontSize={smallFontSize} 
                                                                fontWeight={400} 
                                                                textAnchor="start" 
                                                                transform={`rotate(270, ${textX}, ${textY})`} 
                                                            >
                                                                {value&&'M'} <tspan fontWeight={700} >{value}</tspan>
                                                            </text>
                                                        );
                                                    }} 
                                                    shape={<CustomBar />}
                                                    // activeBar={<Rectangle fill="pink" stroke="blue" />} 
                                                />
                                        
                                                <Bar 
                                                    dataKey="totalScore" 
                                                    fill={`var(--deepGreen)`}
                                                    label={(props) => {
                                                        const { x, y, width, value } = props;
                                                        const textX = x + width / 2;
                                                        return (
                                                            <g>
                                                                <text x={textX} y={y} dy={-15} fill={`var(--gray6)`} fontSize={fontSize} fontWeight={700} textAnchor="middle">total</text>
                                                                <text x={textX} y={y} dy={-3} fill="#000000" fontSize={fontSize} fontWeight={700} textAnchor="middle">{value}</text>
                                                            </g>
                                                        );
                                                    }}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* printGraph */}
                                    <div
                                        style={{
                                        width: "100%",
                                        height: "95%",
                                        }}
                                        className='historicalSHSATReport__printGraph'
                                    >
                                        <ResponsiveContainer width="100%" height="95%">
                                            <BarChart
                                                data={last10Tests}
                                                margin={{
                                                    top: 10,
                                                    right: 0,
                                                    left: 0,
                                                    bottom: 10,
                                                }}
                                                barSize={fontSize*2.5}
                                                barCategoryGap={smallFontSize*2.5}
                                            >
                                                <XAxis dataKey="testInfo"
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }}
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={(props) => <CustomTick {...props} fontSize={`${fontSize*1.5}px`} fontWeight="bold" fill="var(--gray400)" />}                                                    
                                                    interval={0} // 모든 라벨을 보여줌
                                                    />
                                                <YAxis 
                                                    ticks={[0, totalScoreRange[1]]} 
                                                    axisLine={{ stroke: '#C1C1C1', strokeWidth: 2 }} 
                                                    tickLine={{ stroke: 'none' }} 
                                                    tick={{ fontSize: `${fontSize*2}px`, fontWeight: 'bold', fill: 'var(--gray400)' }} />
                                                <Tooltip content={<CustomTooltip payload={undefined} active={undefined} />} />
                                                {allTests.some(test => test.englishScore !== null) && (
                                                    <Bar dataKey="englishScore" 
                                                        fill="#82ca9d" 
                                                        label={(props) => {
                                                            const { x, y, width, value } = props;
                                                            const textX = x + width/2+3; 
                                                            const textY = y +fontSize*(-1.1); // 라벨의 Y 위치
                                                            return (
                                                                <text 
                                                                    x={textX} 
                                                                    y={textY} 
                                                                    fill="#000" 
                                                                    fontSize={smallFontSize*2} 
                                                                    fontWeight={400} 
                                                                    textAnchor="start" 
                                                                    transform={`rotate(270, ${textX}, ${textY})`} 
                                                                >
                                                                    {value&&'ELA'} <tspan fontWeight={700} >{value}</tspan>
                                                                </text>
                                                            );
                                                        }} 
                                                        shape={<CustomBar />}
                                                        // activeBar={<Rectangle fill="pink" stroke="blue" />} 
                                                    />
                                                )}
                                                {allTests.some(test => test.mathScore !== null) && (
                                                    <Bar dataKey="mathScore" 
                                                        fill="#82ca9d" 
                                                        label={(props) => {
                                                            const { x, y, width, height, value } = props;
                                                            const textX = x + width/2+2; 
                                                            const textY = y +smallFontSize*(-1.1); // 라벨의 Y 위치
                                                            return (
                                                                <text 
                                                                    x={textX} 
                                                                    y={textY} 
                                                                    fill="#000" 
                                                                    fontSize={smallFontSize*2} 
                                                                    fontWeight={400} 
                                                                    textAnchor="start" 
                                                                    transform={`rotate(270, ${textX}, ${textY})`} 
                                                                >
                                                                    {value&&'M'} <tspan fontWeight={700} >{value}</tspan>
                                                                </text>
                                                            );
                                                        }} 
                                                        shape={<CustomBar />}
                                                        // activeBar={<Rectangle fill="pink" stroke="blue" />} 
                                                    />
                                                )}
                                                <Bar 
                                                    dataKey="totalScore" 
                                                    fill={`var(--deepGreen)`}
                                                    label={(props) => {
                                                        const { x, y, width, value } = props;
                                                        const textX = x + width / 2;
                                                        return (
                                                            <g>
                                                                <text x={textX} y={y} dy={-36} fill={`var(--gray6)`} fontSize={fontSize*2} fontWeight={700} textAnchor="middle">total</text>
                                                                <text x={textX} y={y} dy={-6} fill="#000000" fontSize={fontSize*2} fontWeight={700} textAnchor="middle">{value}</text>
                                                            </g>
                                                        );
                                                    }}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                </div>
                                
                                {/* storng and week point-Math */}
                                {/* <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__analysis`}>
                                    <div className='historicalSHSATReport__SectionBoxHeader space'>
                                        <div className='historicalSHSATReport__Sectiontitle'>Category Breakdown (% Correct)</div>
                                    </div>
                                    <div className='historicalSHSATReport__analysisWrap'>
                                        {Object.keys(avgCorrectPercent).length > 0 ? (
                                            Object.entries(avgCorrectPercent).map(([subject, categories], index) => (
                                                <div key={index} className='historicalSHSATReport__analysisBox'>
                                                    <div className='historicalSHSATReport__analysisBoxTitle'>{subject}</div>
                                                    <div className='historicalSHSATReport__analysisContentWrap'>
                                                        {Object.entries(categories)?.map(([categoryName, percent], i) => (
                                                            <div key={i} className='historicalSHSATReport__categoryWrap'>
                                                                <div className='historicalSHSATReport__categoryTitle'>{categoryName}</div>
                                                                <LevelBar className={subject==='Math'? 'LevelBar__orangeBox': 'LevelBar__greenBox'} percent = {percent}/>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        ): (
                                            <div className='historicalSHSATReport__analysisBox'>
                                              <div className='historicalSHSATReport__analysisBoxTitle'>-</div>
                                              <div className='historicalSHSATReport__analysisContentWrap'>
                                                <div className='historicalSHSATReport__categoryWrap'>
                                                  <div className='historicalSHSATReport__categoryTitle'>-</div>
                                                  <LevelBar className='LevelBar__defaultBox' percent={0} />
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        }
                                    </div>
                                </div> */}
                                
                                {/* Recent tests score breakdown */}
                                {/* <div className={`historicalSHSATReport__sectionBox historicalSHSATReport__testAch`}>
                                    <div className='historicalSHSATReport__SectionBoxHeader space'>
                                        <div className='historicalSHSATReport__Sectiontitle'>Recent Tests Score Breakdown</div>
                                        <div className='historicalSHSATReport__graphNum'>Last 5 Tests</div>
                                    </div>
                                    <table className='historicalSHSATReport__table'>
                                        <thead>
                                            <tr className='historicalSHSATReport__tableHeader'>
                                                <th className='historicalSHSATReport__tableRowName'>English</th>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((data, i)=>(
                                                    <th>{data.testNumber}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tr className="historicalSHSATReport__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {englishRootCategories?.map((category)=>(
                                            <tr>
                                                <td className='historicalSHSATReport__tableRowName'>{category.length>0?category:'-'}</td>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((test, j) => {
                                                    const englishSection = test.testSections.find(section => section.subject === "Engish Language Arts");
                                                    return (
                                                        <td key={j}>
                                                            {englishSection?.rootCategories[category]?.numQuestions > 0 
                                                                ? isNaN(Math.round((englishSection.rootCategories[category]?.numCorrect / englishSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                    ? '-' 
                                                                    : `${Math.round((englishSection.rootCategories[category]?.numCorrect / englishSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                : '-'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </table>
                                    <tr className="historicalSHSATReport__tableSpacing">
                                            <td colSpan={4}></td> 
                                    </tr>
                                    <table className='historicalSHSATReport__table'>
                                        <thead>
                                            <tr className='historicalSHSATReport__tableHeader'>
                                                <th className='historicalSHSATReport__tableRowName'>Math</th>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((data, i)=>(
                                                    <th>{data.testNumber}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tr className="historicalSHSATReport__tableSpacingRow">
                                            <td colSpan={4}></td> 
                                        </tr>
                                        {mathRootCategories?.map((category)=>(
                                            <tr>
                                                <td className='historicalSHSATReport__tableRowName'>{category.length>0?category:'-'}</td>
                                                {Array.from({ length: Math.max(0, 3 - tests.length) }).map((_, idx) => (
                                                    <th key={-idx} /> 
                                                ))}
                                                {tests.map((test, j) => {
                                                    const mathSection = test.testSections.find(section => section.subject === "Math");
                                                    return (
                                                        <td key={j}>
                                                            {mathSection?.rootCategories[category]?.numQuestions > 0 
                                                                ? isNaN(Math.round((mathSection.rootCategories[category]?.numCorrect / mathSection.rootCategories[category]?.numQuestions) * 100)) 
                                                                    ? '-' 
                                                                    : `${Math.round((mathSection.rootCategories[category]?.numCorrect / mathSection.rootCategories[category]?.numQuestions) * 100)}%`
                                                                : '-'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </table>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            :
            <Loader />}
        </div>
    );      
}

export default HistoricalSHSATReport;